import type { ReactNode } from 'react';

import type { Language } from '../../generated/graphql-viewer';

import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { useViewerAccess } from './viewer-access-provider';
import { useGetWebcastStreamNewAuthQuery } from '../../generated/graphql-viewer';
import { useAbsoluteRoutes } from '../../routes';
import { devLog } from '../../utils';

type LanguageContextValue = {
  availableLanguages?: Language[];
  selectedLanguage?: Language;
  primaryLanguage?: Language;
  selectLanguage: (language: string) => void;
  isCustomLanguage: (language?: string) => boolean;
};

const initialLanguageContext: LanguageContextValue = {
  availableLanguages: undefined,
  selectedLanguage: undefined,
  primaryLanguage: undefined,
  selectLanguage: () => {},
  isCustomLanguage: () => false,
};

export const LanguageContext = createContext<LanguageContextValue>(initialLanguageContext);

type Props = {
  children: ReactNode;
};

export function SelectedLanguageProvider({ children }: Props) {
  const { i18n } = useTranslation();
  const absoluteRoutes = useAbsoluteRoutes();
  const { data: getWebcastViewerResponse, loading, headers, hasAuthHeaders } = useViewerAccess();
  const { webcastId = '', language: urlLanguage } = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const hmacToken = searchParams.get('hmac-token') || '';

  const accessGranted = getWebcastViewerResponse?.webcastNewAuth?.authenticationWorkflow?.accessGranted;

  const { data: getWebcastStreamResponse } = useGetWebcastStreamNewAuthQuery({
    variables: { webcastId },
    ...(hasAuthHeaders && { context: { headers } }),
    skip: loading || accessGranted === false || !webcastId,
    onCompleted: () => {
      if (
        typeof window !== 'undefined' &&
        window.localStorage &&
        window.localStorage.getItem('lspro::debug') === 'true'
      ) {
        console.log('⚙️ useGetWebcastStreamNewAuthQuery called in SelectedLanguageProvider');
      }
    },
  });

  const primaryLanguage = getWebcastViewerResponse?.webcastNewAuth?.primaryLang;
  const additionalLanguages = getWebcastViewerResponse?.webcastNewAuth?.additionalLangs || [];
  const vod = getWebcastStreamResponse?.webcastNewAuth?.streaming?.vod;

  const eventLanguages = [primaryLanguage, ...additionalLanguages].filter((language) => language !== undefined);

  const vodLanguages: Language[] =
    vod?.streams?.map(({ metadata }) => {
      const code = metadata.language || '';
      const language = eventLanguages.find((language) => language.code === code);

      if (language?.__typename === 'CustomLanguage') {
        return {
          __typename: 'CustomLanguage',
          code,
          name: language.name || '',
        };
      }

      return {
        __typename: 'StandardLanguage',
        code,
      };
    }) || [];

  const availableLanguages = primaryLanguage ? (vod?.enabled ? vodLanguages : eventLanguages) : undefined;

  const [selectedLanguage, setSelectedLanguage] = useState<Language>();

  const selectedLanguageIsCustom = selectedLanguage?.__typename === 'CustomLanguage';
  const selectedLanguageIsValid =
    selectedLanguage !== undefined &&
    availableLanguages?.find(({ code }) => code === selectedLanguage.code) !== undefined;

  const selectLanguage = (language: string) => {
    setSelectedLanguage(availableLanguages?.find(({ code }) => code === language));
  };

  const isCustomLanguage = (language?: string) =>
    availableLanguages?.find(({ code }) => code === language)?.__typename === 'CustomLanguage';

  useEffect(() => {
    if (!primaryLanguage || !availableLanguages || selectedLanguage) return;

    setSelectedLanguage(urlLanguage ? availableLanguages?.find(({ code }) => code === urlLanguage) : primaryLanguage);
  }, [availableLanguages, primaryLanguage, selectedLanguage, urlLanguage]);

  useEffect(() => {
    if (!primaryLanguage || hmacToken) return;

    const language = selectedLanguageIsValid ? selectedLanguage : primaryLanguage;
    const newLanguage = selectedLanguageIsCustom ? primaryLanguage.code : language.code;

    i18n.changeLanguage(newLanguage);
    document.documentElement.setAttribute('lang', newLanguage);

    const urlSearchParams = new URLSearchParams(window.location.search);
    const { code } = Object.fromEntries(urlSearchParams.entries());

    if (!code) navigate(`${absoluteRoutes.viewer}/${webcastId}/${language.code}`);
  }, [
    absoluteRoutes.viewer,
    hmacToken,
    i18n,
    navigate,
    primaryLanguage,
    selectedLanguage,
    selectedLanguageIsCustom,
    selectedLanguageIsValid,
    webcastId,
  ]);

  return (
    <LanguageContext.Provider
      value={{ availableLanguages, selectedLanguage, primaryLanguage, selectLanguage, isCustomLanguage }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export const useSelectedLanguage = () => useContext(LanguageContext);
